import Route from 'route-parser'
import { combineLatest, of } from 'rxjs'
import { map, filter, distinctUntilChanged, catchError } from 'rxjs/operators'
import { mergeResponses, isValidResponse, mapResponse } from 'libraries/front-end-core/helpers/dataflow'
import { RESPONSE_UNAUTHORIZED, RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { AOL_ORDER_PREFERENCES, EMAIL_PREFERENCES, SEARCH_HISTORY, USER, USER_AUTH, PRIMARY_AOL, PRACTISING_AOL } from 'constants/requests'
import { URL_SEARCH_HISTORY, URL_PASSWORD_RESET, URL_EMAIL_PREFERENCES, URL_PRIMARY_AOL, URL_PRACTISING_AOL } from 'constants/urls'
import { DayMap } from 'requests/session/transforms'
import { fetch, observe } from 'services/ApiService'
import observeRequest from 'helpers/fcObserveRequest'
import { AOL_CORPORATE, DEFAULT_AOL_ORDER } from 'constants/aol/aol'

const searchHistoryRoute = new Route(URL_SEARCH_HISTORY)
const passwordResetRoute = new Route(URL_PASSWORD_RESET)

export const $user = fetch(USER)

export const User = combineLatest(
  [$user, fetch(USER_AUTH)],
  mergeResponses
)

export const $emailPreferences = fetch(EMAIL_PREFERENCES)

export const $isLogged = $user.pipe(map(x =>
  x === RESPONSE_PENDING
    ? RESPONSE_PENDING
    : x === RESPONSE_UNAUTHORIZED
      ? false
      : isValidResponse(x)
), map(mapResponse(isLogged => ({ isLogged }))))

export const $userAOL = $user.pipe(map(x =>
  x === RESPONSE_PENDING
    ? RESPONSE_PENDING
    : x === RESPONSE_UNAUTHORIZED
      ? null
      : isValidResponse(x)
), map(mapResponse(areaOfLaw => ({ areaOfLaw }))))

export const UserUrn = User.pipe(filter(isValidResponse), map(x => x.uri), distinctUntilChanged())

export const SearchHistory = observe(SEARCH_HISTORY, { length: 20 })

export const pushSearchHistory = (searchTerm, searchType) =>
  observeRequest({
    url: searchHistoryRoute.reverse(),
    headers: { 'Content-Type': 'application/json' },
    method: 'post',
    body: JSON.stringify({ searchTerm, searchType })
  }).subscribe()

export const submitPasswordReset = (body) =>
  observeRequest({
    url: passwordResetRoute.reverse(),
    headers: { 'Content-Type': 'application/json' },
    method: 'post',
    body: JSON.stringify(body),
    withErrorBody: true
  }).pipe(
    map(resp => {
      if (resp && typeof resp === 'object' && 'body' in resp) {
        try {
          const data = JSON.parse(resp.body)
          if (data && data.acceptable === false) {
            return {
              success: false,
              weakPassword: true,
              warning: data.warning || undefined,
              suggestions: Array.isArray(data.suggestions) ? data.suggestions : []
            }
          }
        } catch (_) {}
        return { success: false, weakPassword: false }
      }
      return typeof resp === 'string' ? { success: true } : { success: false, weakPassword: false }
    })
  )

export const updateEmailPreferences = (preferences) => {
  const {
    areaOfLaw,
    caEmails: enabled,
    caEmailDays
  } = preferences

  const payload = {
    areaOfLaw,
    enabled,
    ...Object.keys(DayMap).reduce((acc, curr) => ({
      ...acc,
      [curr]: caEmailDays.includes(DayMap[curr])
    }), {})
  }

  return observeRequest({
    url: URL_EMAIL_PREFERENCES,
    headers: { 'Content-Type': 'application/json' },
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export const updateSharedEmailPreferences = (preferences) => {
  const {
    caEmailDays,
    receiveNoNews,
    areaOfLawPrefs
  } = preferences

  const payload = {
    receiveNoNews,
    ...Object.keys(DayMap).reduce((acc, curr) => ({
      ...acc,
      [curr]: caEmailDays.includes(DayMap[curr])
    }), {}),
    areaOfLawPrefs
  }

  return observeRequest({
    url: URL_EMAIL_PREFERENCES,
    headers: { 'Content-Type': 'application/json' },
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

export const $userAolPreferences = fetch(AOL_ORDER_PREFERENCES).pipe(
  map(resp => resp === RESPONSE_UNAUTHORIZED ? DEFAULT_AOL_ORDER : resp),
  catchError(() => of(DEFAULT_AOL_ORDER))
)

export const $primaryAol = fetch(PRIMARY_AOL).pipe(
  map(resp => resp === RESPONSE_UNAUTHORIZED ? AOL_CORPORATE : resp),
  catchError(() => of(AOL_CORPORATE))
)

export const $practisingAol = fetch(PRACTISING_AOL).pipe(
  map(resp => resp === RESPONSE_UNAUTHORIZED ? '' : resp),
  catchError(() => of(''))
)

export const updatePrimaryAol = (areaOfLaw) =>
  observeRequest({
    url: URL_PRIMARY_AOL,
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ areaOfLaw })
  })

export const updatePractisingAol = (practisingAreaOfLaw) =>
  observeRequest({
    url: URL_PRACTISING_AOL,
    method: 'put',
    headers: { 'Content-Type': 'application/json' },
    body: practisingAreaOfLaw
  }).pipe(
    catchError(() => of(practisingAreaOfLaw))
  )
